import type React from 'react';

import type { CellContext, HeaderContext } from '@tanstack/react-table';
import type { List } from '@vlabs/api/artemy/model';
import type { TBuildTableViewCell } from '@vlabs/monokit/shared/ui/table-view';
import { buildTableViewCell } from '@vlabs/monokit/shared/ui/table-view';

type TSharedCtx<TValue = unknown> =
  | CellContext<List, TValue>
  | HeaderContext<List, TValue>;

const dataTestIdFromCtx = <TValue extends React.ReactNode>(
  ctx: TSharedCtx<TValue>,
): {
  'data-testid': string;
} | undefined => {
  if (!ctx) return undefined;
  if ((ctx as CellContext<List, TValue>).row) {
    return {
      'data-testid': `${ctx.column.id}.cell.${(ctx as CellContext<List, TValue>).row.original.list_id}`,
    };
  }
  return {
    'data-testid': `${ctx.column.id}.header`,
  };
};

export function renderHeader<TValue extends React.ReactNode>(
  renderer: (ctx: TSharedCtx<TValue>) => TBuildTableViewCell,
  value: React.ReactNode = '',
) {
  return (header: HeaderContext<List, TValue>): React.JSX.Element => renderer(header)(value);
}

export function renderCell<TValue extends React.ReactNode>(
  renderer: (ctx: TSharedCtx<TValue>) => TBuildTableViewCell,
  wrap?: (ctx: CellContext<List, TValue>) => React.ReactNode,
) {
  return (ctx: CellContext<List, TValue>): React.JSX.Element => renderer(ctx)(
    wrap
      ? wrap?.(ctx)
      : ctx.getValue(),
  );
}

export const titleCell = (ctx: TSharedCtx<string>): TBuildTableViewCell => buildTableViewCell({
  style: {
    flex: '0 0 320px',
    padding: '16px 16px 16px 0',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  ...dataTestIdFromCtx<string>(ctx),
});

export const createTimeCell = (ctx: TSharedCtx<string>): TBuildTableViewCell => buildTableViewCell({
  style: {
    flex: '0 0 200px',
  },
  ...dataTestIdFromCtx<string>(ctx),
});

export const lastUpdateTimeCell = (ctx: TSharedCtx<string>): TBuildTableViewCell => buildTableViewCell({
  style: {
    flex: '0 0 200px',
    maxWidth: '200px',
    minWidth: '200px',
  },
  ...dataTestIdFromCtx<string>(ctx),
});

export const showCardsCell = (ctx: TSharedCtx<string>): TBuildTableViewCell => buildTableViewCell({
  style: {
    display: 'flex',
    flex: '0 0 162px',
    maxWidth: '162px',
    minWidth: '162px',
    padding: '0 16px',
    justifyContent: 'center',
  },
  ...dataTestIdFromCtx<string>(ctx),
});

export const ownerDepartmentCell = (ctx: TSharedCtx<string>): TBuildTableViewCell => buildTableViewCell({
  style: {
    flex: '0 0 250px',
    padding: '16px',
  },
  ...dataTestIdFromCtx<string>(ctx),
});

export const spacerCell = buildTableViewCell({
  style: {
    flex: '1 0 0',
    minWidth: '1px',
    padding: '16px',
  },
});

export const actionCell = (ctx: TSharedCtx<string>): TBuildTableViewCell => buildTableViewCell({
  style: {
    flex: '0 0 40px',
    padding: 0,
  },
  ...dataTestIdFromCtx<string>(ctx),
});
export const dividerCell = buildTableViewCell({
  style: {
    flex: '0 0 16px',
    padding: 0,
  },
});
